import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StpRegistrationStorageService } from '../../../services/stp-registration-storage.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { SYSCountry } from '../../../models/sys_country.model';
import { District, Division, Upazila } from 'src/app/shared/models/common.model';
import { STPLocation } from '../../../models/stp_location.model';
import { PaymentDto } from 'src/app/shared/models/dto/payment-dto.model';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-correction-request-stp',
  templateUrl: './correction-request-stp.component.html',
  styleUrls: ['./correction-request-stp.component.scss']
})
export class CorrectionRequestStpComponent implements OnInit {

  correctionRequestForm!: FormGroup;
  stpMainId: number;
  stpMainInfo: any;
  stpLocations: STPLocation[] = [];
  sysCountries: SYSCountry[] = [];
  divisions: Division[] = [];
  districts: District[] = [];
  upazilas: Upazila[] = [];
  setupInfo: any;
  paymentMessage: string;
  isSubmitButtonEnable: boolean = false;
  userInfo: any;

  // the location ("Address of the Organization") can't be corrected, as in renewal / re-apply:
  // the application id carries the district code. Only the STP name can be changed here.
  private readonly lockedLocationControls = [
    'locationId',
    'countryId',
    'divisionId',
    'districtId',
    'upazilaId',
    'postOffice',
    'address',
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private stpRegistrationStorageService: StpRegistrationStorageService,
    private notification: NzNotificationService,
    private router: Router
  ) {
    this.stpMainId = this.route.snapshot.params.id;
  }

  ngOnInit() {
    this.correctionRequestForm = this.fb.group({
      id: [null],
      stpMainId: [null],
      prevStpName: [null],
      prevStpNameBangla: [null],
      prevLocationId: [null],
      prevLocation: [null],
      prevCountryId: [null],
      prevCountry: [null],
      prevDivisionId: [null],
      prevDivision: [null],
      prevDistrictId: [null],
      prevDistrict: [null],
      prevUpazilaId: [null],
      prevUpazila: [null],
      prevPostOffice: [null],
      prevAddress: [null],
      stpName: ['', [Validators.pattern(/^[a-zA-Z\s.]*$/)]],
      stpNameBangla: ['', [Validators.pattern(/^[\u0980-\u09FF\s,]*$/)]],
      locationId: [null, [Validators.required]],
      countryId: [null, [Validators.required]],
      divisionId: [null, [Validators.required]],
      districtId: [null, [Validators.required]],
      upazilaId: [null, [Validators.required]],
      postOffice: ['', [Validators.required]],
      address: ['', [Validators.required]]
    });
    this.lockedLocationControls.forEach((name) =>
      this.correctionRequestForm.get(name)?.disable({ emitEvent: false })
    );

    this.getStpMainInfo();
    this.getSetupInfo();
  }

  getStpMainInfo() {
    this.stpRegistrationStorageService.readBasicProfileById(this.stpMainId)
      .subscribe(
        (res) => {
          if (res.success) {
            this.stpMainInfo = res?.data;
            this.correctionRequestForm.patchValue({
              stpMainId: this.stpMainId,
              prevStpName: res?.data?.stpName,
              prevStpNameBangla: res?.data?.stpNameBangla,
              prevLocationId: res?.data?.stpLocation?.id,
              prevLocation: res?.data?.stpLocation?.locationName,
              prevCountryId: res?.data?.sysCountry?.id,
              prevCountry: res?.data?.sysCountry?.countryName,
              prevDivisionId: res?.data?.sysDivision?.id,
              prevDivision: res?.data?.sysDivision?.divisionName,
              prevDistrictId: res?.data?.sysDistrict?.id,
              prevDistrict: res?.data?.sysDistrict?.districtName,
              prevUpazilaId: res?.data?.sysUpazilla?.id,
              prevUpazila: res?.data?.sysUpazilla?.upazillaName,
              prevPostOffice: res?.data?.postOffice,
              prevAddress: res?.data?.address
            });

            this.getStpLocations();
          } else {
            this.notification.error('Failed', 'STP application not found !');
          }
        },
        (error) => {
          this.notification.error('Failed', 'STP application not found !');
        }
      );
  }

  getCorrectionRequestInfoByStpMainId() {
    this.stpRegistrationStorageService.getCorrectionRequestInfoByStpMainId(this.stpMainId)
      .subscribe(
        (res) => {
          if (res.success) {
            this.patchEditableForm(res?.data);
          } else {
            this.patchEditableForm(this.stpMainInfo);
          }
        },
        (error) => {
          this.patchEditableForm(this.stpMainInfo);
        }
      );
  }

  getSetupInfo() {
    this.stpRegistrationStorageService.getSetupInfo()
      .subscribe((res) => {
        if(res?.success){
          this.setupInfo = res?.data;

          if (this.setupInfo?.isPaymentApplicable) {
            this.paymentMessage = 'Are you sure to pay ' + this.setupInfo?.paymentAmount + ' taka online and submit this correction request application?';
          }
        }
      });
  }

  getStpLocations(){
    this.stpRegistrationStorageService.readStpLocations().subscribe({
      next: (res) => {
        this.stpLocations = res;
        this.getCorrectionRequestInfoByStpMainId();
      },
    });
  }

  setCountriesByLocationTypeId(): void {
    if (this.correctionRequestForm.get('locationId')?.value) {
      this.stpRegistrationStorageService.readStpCountries().subscribe({
        next: (res) => {
          this.sysCountries = res;
        },
      });
    } else {
      this.correctionRequestForm.get('countryId')?.reset();
    }
  }

  setDivisionByCountryId(): void {
    if (this.correctionRequestForm.get('countryId')?.value) {
      this.stpRegistrationStorageService.readStpDivisionsBYCountryID(this.correctionRequestForm.get('countryId')?.value)
        .subscribe({
          next: (res) => {
            this.divisions = res;
          },
        });
    } else {
      this.correctionRequestForm.get('divisionId')?.reset();
    }
  }

  setDistrictInformation(): void {
    if (this.correctionRequestForm.get('divisionId')?.value) {
      this.stpRegistrationStorageService.readStpDistrictsByDivisionId(this.correctionRequestForm.get('divisionId')?.value)
        .subscribe({
          next: (res) => {
            this.districts = res;
          },
        });
    } else {
      this.correctionRequestForm.get('districtId')?.reset();
    }
  }

  setStpUpazillaInformation(): void {
    if (this.correctionRequestForm.get('districtId')?.value) {
      this.stpRegistrationStorageService.readStpUpazillasByDistrictId(this.correctionRequestForm.get('districtId')?.value)
        .subscribe({
          next: (res) => {
            this.upazilas = res;
          },
        });
    } else {
      this.correctionRequestForm.get('upazilaId')?.reset();
    }
  }

  saveCorrectionRequestInfo(){
    this.stpRegistrationStorageService.saveCorrectionRequestInfo(this.getCorrectionRequestPayload()).subscribe({
      next: (res) => {
        if(res?.success){
          this.notification.success('Success', res?.message);
          this.isSubmitButtonEnable = true;
          this.correctionRequestForm.patchValue({
            id: res?.data?.id ?? this.correctionRequestForm.get('id')?.value ?? null,
            stpMainId: res?.data?.stpMain?.id ?? res?.data?.stpMainId ?? this.stpMainId
          });
          this.getCorrectionRequestInfoByStpMainId();
        }else{
          this.notification.warning('Warning', res?.message);
        }
      },
      error: (error) => this.notification.error('Failed', error.message),
    });
  }

  submitCorrectionRequestInfo(){
    this.stpRegistrationStorageService.submitCorrectionRequestInfo(this.getCorrectionRequestPayload()).subscribe({
      next: (res) => {
        if(res?.success){
          this.notification.success('Success', res?.message);
          this.router.navigate(['home/stp-registration/application-list-stp']);
        }else{
          this.notification.warning('Warning', res?.message);
        }
      },
      error: (error) => this.notification.error('Failed', error.message),
    });
  }

  onlinePaymentforStpCorrectionRequest(){
    if(this.correctionRequestForm.get('id')?.value){
      this.userInfo = JSON.parse(localStorage.getItem('currentUserInfo') || '{}');

      let requestData = new PaymentDto();
      requestData.userId = this.userInfo.id;
      requestData.amount = this.setupInfo?.paymentAmount;

      this.stpRegistrationStorageService.stpCorrectionOnlinePayment(requestData)
      .subscribe((response) => {
        window.location.href = response.data;
      });
    } else {
      this.notification.error('Failed', 'Please fill up the information first !');
    }
  }

  private patchEditableForm(data: any): void {
    if (!data) {
      return;
    }

    const correctionRequestId = data?.id ?? null;
    const stpMainId = data?.stpMainId ?? data?.stpMain?.id ?? this.stpMainId;
    // the location always shows the STP's current one (a request saved before the lock may differ)
    const location = this.stpMainInfo ?? data;
    const locationId = location?.stpLocation?.id ?? null;
    const countryId = location?.sysCountry?.id ?? null;
    const divisionId = location?.sysDivision?.id ?? null;
    const districtId = location?.sysDistrict?.id ?? null;
    const upazilaId = location?.sysUpazila?.id ?? location?.sysUpazilla?.id ?? null;

    this.correctionRequestForm.patchValue({
      id: correctionRequestId,
      stpMainId,
      stpName: data?.stpName ?? null,
      stpNameBangla: data?.stpNameBangla ?? null,
      locationId,
      countryId: null,
      divisionId: null,
      districtId: null,
      upazilaId: null,
      postOffice: location?.postOffice ?? null,
      address: location?.address ?? null
    });

    if (!locationId) {
      return;
    }

    this.stpRegistrationStorageService.readStpCountries().pipe(
      tap((countries) => {
        this.sysCountries = countries;
        this.correctionRequestForm.patchValue({ countryId });
      }),
      switchMap(() => {
        if (!countryId) {
          return of(null);
        }

        return this.stpRegistrationStorageService.readStpDivisionsBYCountryID(countryId).pipe(
          tap((divisions) => {
            this.divisions = divisions;
            this.correctionRequestForm.patchValue({ divisionId });
          })
        );
      }),
      switchMap(() => {
        if (!divisionId) {
          return of(null);
        }

        return this.stpRegistrationStorageService.readStpDistrictsByDivisionId(divisionId).pipe(
          tap((districts) => {
            this.districts = districts;
            this.correctionRequestForm.patchValue({ districtId });
          })
        );
      }),
      switchMap(() => {
        if (!districtId) {
          return of(null);
        }

        return this.stpRegistrationStorageService.readStpUpazillasByDistrictId(districtId).pipe(
          tap((upazilas) => {
            this.upazilas = upazilas;
            this.correctionRequestForm.patchValue({ upazilaId });
          })
        );
      })
    ).subscribe();
  }

  private getCorrectionRequestPayload(): any {
    const formValue = this.correctionRequestForm.getRawValue();

    return {
      id: formValue.id ?? null,
      stpMainId: Number(formValue.stpMainId ?? this.stpMainId),
      prevStpName: formValue.prevStpName ?? null,
      prevStpNameBangla: formValue.prevStpNameBangla ?? null,
      prevLocationId: formValue.prevLocationId ?? null,
      prevCountryId: formValue.prevCountryId ?? null,
      prevDivisionId: formValue.prevDivisionId ?? null,
      prevDistrictId: formValue.prevDistrictId ?? null,
      prevUpazilaId: formValue.prevUpazilaId ?? null,
      prevPostOffice: formValue.prevPostOffice ?? null,
      prevAddress: formValue.prevAddress ?? null,
      stpName: formValue.stpName ?? null,
      stpNameBangla: formValue.stpNameBangla ?? null,
      locationId: formValue.locationId ?? null,
      countryId: formValue.countryId ?? null,
      divisionId: formValue.divisionId ?? null,
      districtId: formValue.districtId ?? null,
      upazilaId: formValue.upazilaId ?? null,
      postOffice: formValue.postOffice ?? null,
      address: formValue.address ?? null,
    };
  }
}
